import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, ArrowRight, Calendar,
  FileText, DollarSign, Calculator, TrendingUp,
  Star, BookOpen
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const BlogPage = () => {
  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Posts do blog
  const posts = [
    {
      id: 1,
      slug: '/nota-fiscal-mei',
      titulo: 'Como Emitir Nota Fiscal MEI: Guia Completo 2025',
      descricao: 'Aprenda passo a passo como emitir nota fiscal sendo MEI. Tipos de nota, quando é obrigatório e como fazer pelo celular.',
      imagem: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
      categoria: 'Nota Fiscal',
      categoriaCor: 'emerald',
      data: '15 Jan 2025',
      tempoLeitura: '8 min',
      destaque: true
    },
    {
      id: 2,
      slug: '/declaracao-anual-mei',
      titulo: 'Declaração Anual MEI (DASN-SIMEI): Como Fazer em 2025',
      descricao: 'Guia completo para fazer sua Declaração Anual do MEI. Prazos, passo a passo, multas por atraso e dicas importantes.',
      imagem: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop',
      categoria: 'Obrigações',
      categoriaCor: 'purple',
      data: '10 Jan 2025',
      tempoLeitura: '10 min',
      destaque: true
    },
    {
      id: 3,
      slug: '/das-mei-2025',
      titulo: 'DAS MEI 2025: Valor Atualizado, Como Pagar e Evitar Multas',
      descricao: 'Tudo sobre o DAS MEI em 2025: novos valores, formas de pagamento, o que fazer se atrasar.',
      imagem: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400&h=300&fit=crop',
      categoria: 'Pagamentos',
      categoriaCor: 'blue',
      data: '05 Jan 2025',
      tempoLeitura: '6 min',
      destaque: false
    },
    {
      id: 4,
      slug: '/limite-faturamento-mei',
      titulo: 'Limite de Faturamento MEI 2025: O que Mudou e Como Calcular',
      descricao: 'Entenda o limite de faturamento do MEI em 2025, o que acontece se ultrapassar.',
      imagem: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
      categoria: 'Gestão',
      categoriaCor: 'amber',
      data: '02 Jan 2025',
      tempoLeitura: '5 min',
      destaque: false
    },
    {
      id: 5,
      slug: '/o-que-e-mei',
      titulo: 'O que é MEI? Guia Completo do Microempreendedor Individual',
      descricao: 'Entenda o que é MEI, quem pode ser, benefícios, custos e como abrir.',
      imagem: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      categoria: 'Guia',
      categoriaCor: 'emerald',
      data: '01 Jan 2025',
      tempoLeitura: '12 min',
      destaque: false
    },
    {
      id: 6,
      slug: '/como-abrir-mei',
      titulo: 'Como Abrir MEI: Passo a Passo Completo 2025',
      descricao: 'Abra seu MEI online e grátis em menos de 15 minutos.',
      imagem: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
      categoria: 'Abertura',
      categoriaCor: 'purple',
      data: '01 Jan 2025',
      tempoLeitura: '10 min',
      destaque: false
    },
    {
      id: 7,
      slug: '/mei-inss',
      titulo: 'MEI e INSS: Aposentadoria e Benefícios Previdenciários',
      descricao: 'Entenda todos os direitos do MEI no INSS: aposentadoria, auxílio-doença.',
      imagem: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=300&fit=crop',
      categoria: 'Benefícios',
      categoriaCor: 'blue',
      data: '01 Jan 2025',
      tempoLeitura: '8 min',
      destaque: false
    },
  ];

  // Cores das categorias
  const categoryColors = {
    emerald: { bg: '#10b981', light: '#d1fae5', text: '#059669' },
    purple: { bg: '#8b5cf6', light: '#ede9fe', text: '#7c3aed' },
    blue: { bg: '#3b82f6', light: '#dbeafe', text: '#2563eb' },
    amber: { bg: '#f59e0b', light: '#fef3c7', text: '#d97706' }
  };

  // Posts em destaque
  const postsDestaque = posts.filter(p => p.destaque);

  return (
    <div className="min-h-screen font-sans bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <BookOpen className="w-10 h-10" />
            </div>

            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Blog <span className="text-yellow-300">MeuMei</span>
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto opacity-90">
              Artigos, guias e dicas para você, Microempreendedor Individual.
            </p>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Posts em Destaque */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Star className="w-6 h-6 text-amber-500" />
            <h2 className="text-2xl font-bold text-gray-800">Em Destaque</h2>
          </div>

          {/* Grid de Destaque - 2 colunas */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '64px' }}>
            {postsDestaque.map((post) => {
              const colors = categoryColors[post.categoriaCor] || categoryColors.emerald;
              return (
                <div
                  key={post.id}
                  onClick={() => navigateTo(post.slug)}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.3s'
                  }}
                  className="hover:shadow-xl"
                >
                  {/* Imagem */}
                  <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                    <img 
                      src={post.imagem}
                      alt={post.titulo}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }} />
                    
                    {/* Categoria */}
                    <span 
                      style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        padding: '6px 12px',
                        borderRadius: '9999px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'white',
                        backgroundColor: colors.bg
                      }}
                    >
                      {post.categoria}
                    </span>
                  </div>

                  {/* Conteúdo */}
                  <div style={{ padding: '24px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', lineHeight: '1.4' }}>
                      {post.titulo}
                    </h3>
                    <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px', lineHeight: '1.5' }}>
                      {post.descricao}
                    </p>

                    {/* Meta */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: '#9ca3af' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar style={{ width: '16px', height: '16px' }} />
                          {post.data}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock style={{ width: '16px', height: '16px' }} />
                          {post.tempoLeitura}
                        </span>
                      </div>
                      <ArrowRight style={{ width: '20px', height: '20px', color: '#10b981' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Todos os Posts */}
          <h2 className="text-2xl font-bold text-gray-800 mb-8">Todos os Artigos</h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {posts.map((post) => {
              const colors = categoryColors[post.categoriaCor] || categoryColors.emerald;
              return (
                <div
                  key={post.id}
                  onClick={() => navigateTo(post.slug)}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '24px',
                    padding: '24px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.3s',
                    alignItems: 'center'
                  }}
                  className="hover:shadow-lg"
                >
                  {/* Imagem - TAMANHO FIXO */}
                  <div style={{ 
                    width: '180px', 
                    minWidth: '180px',
                    maxWidth: '180px',
                    height: '120px', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    <img 
                      src={post.imagem}
                      alt={post.titulo}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>

                  {/* Conteúdo */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Categoria */}
                    <span 
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        backgroundColor: colors.light,
                        color: colors.text
                      }}
                    >
                      {post.categoria}
                    </span>

                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                      {post.titulo}
                    </h3>
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {post.descricao}
                    </p>

                    {/* Meta */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '14px', color: '#9ca3af' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar style={{ width: '14px', height: '14px' }} />
                        {post.data}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '14px', height: '14px' }} />
                        {post.tempoLeitura}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <ArrowRight style={{ width: '24px', height: '24px', color: '#d1d5db', flexShrink: 0 }} />
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div
            style={{
              marginTop: '64px',
              background: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
              borderRadius: '24px',
              padding: '48px',
              textAlign: 'center',
              color: 'white'
            }}
          >
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>
              🚀 Comece a usar o MeuMei
            </h2>
            <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
              Chega de complicação! Gerencie seu MEI de forma simples.
            </p>
            <button
              onClick={() => navigateTo('https://app.meumei.net/register')}
              style={{
                padding: '16px 32px',
                backgroundColor: 'white',
                color: '#059669',
                fontWeight: 'bold',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Criar Conta Grátis - 7 Dias Free
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;